import type { TargetTaxonomyKey } from '../../Generated/TargetTaxonomy.ts'
import type { DraftState, PlannedOrderSettings, SheetPrimerPairs, SheetState, WizardQuestion } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { findKnownPair, normalizePrimerRegistry } from '../../Util/orderSettingDetection/primerRegistry.ts'
import type { KnownPrimerPair } from '../../Util/orderSettingDetection/primerRegistry.ts'
import type { RegistryRow } from './discovery.ts'
import { namesForPair } from './discovery.ts'
import { primerRevision } from './sheet/sheetMapping.ts'
import { sheetPrimers } from './sheet/sheetImport.ts'
import { chooseSampleConfiguration, withTargetTaxonomy } from './orderSettingsPlan.ts'

/**
 * Where the order's target can come from besides the lab: the registered pair the reads open with, and
 * the registered pairs the sheet's primers name. Either decides the target alone; when both name one and
 * they differ, the lab is asked.
 */

type Finding = { taxon?: TargetTaxonomyKey; candidates: TargetTaxonomyKey[] }

const NOTHING: Finding = { candidates: [] }

const findingOf = (amplicons: readonly TargetTaxonomyKey[]): Finding => {
  const specific = [...new Set(amplicons.filter((amplicon) => amplicon !== 'OTHER'))]
  if (specific.length === 1) {
    return { taxon: specific[0], candidates: specific }
  }
  if (specific.length === 0) {
    return amplicons.length > 0 ? { taxon: 'OTHER', candidates: ['OTHER'] } : NOTHING
  }
  return { candidates: specific }
}

const readsFinding = (draft: DraftState): Finding => {
  const primers = draft.discovery?.primers
  if (primers?.status === 'found') {
    return findingOf(primers.pair.amplicons)
  }
  if (primers?.status === 'partial') {
    return findingOf(primers.candidates.flatMap((pair) => pair.amplicons))
  }
  return primers?.status === 'mixed' ? { candidates: findingOf(primers.pairs.flatMap((pair) => pair.amplicons)).candidates } : NOTHING
}

const hasPrimerColumns = (sheet: SheetState) => !!sheet.mapping.forwardPrimer || !!sheet.mapping.reversePrimer

/** The registered pairs the sheet's primer columns name; `undefined` while they are not identified for the columns as mapped now. */
export const sheetPrimerPairs = (sheet: SheetState | undefined): SheetPrimerPairs | null | undefined => {
  if (!sheet || !hasPrimerColumns(sheet)) {
    return null
  }
  const identified = sheet.registeredPrimers
  return identified && identified.revision === primerRevision(sheet) ? identified.found : undefined
}

const sheetFinding = (draft: DraftState): Finding => {
  const pairs = sheetPrimerPairs(draft.sheet)
  return pairs ? findingOf(pairs.amplicons) : NOTHING
}

/** The registered pairs the sheet's rows name with their primers, against the primer registry. */
export const identifySheetPrimers = (sheet: SheetState, rows: readonly RegistryRow[] | null): SheetState['registeredPrimers'] => {
  const revision = primerRevision(sheet)
  if (!rows || !hasPrimerColumns(sheet)) {
    return { revision, found: null }
  }
  const registry = normalizePrimerRegistry(rows)
  const pairs = new Map<string, KnownPrimerPair>()
  for (const row of sheet.rows) {
    const { forward, reverse } = sheetPrimers(row, sheet.mapping)
    const pair = forward && reverse ? findKnownPair({ forward, reverse }, registry) : null
    if (pair) {
      pairs.set(pair.id, pair)
    }
  }
  if (pairs.size === 0) {
    return { revision, found: null }
  }
  const found = [...pairs.values()]
  return {
    revision,
    found: {
      amplicons: [...new Set(found.flatMap((pair) => pair.amplicons))],
      names: [...new Set(found.flatMap((pair) => namesForPair(pair, rows)))]
    }
  }
}

/**
 * Before the order exists, a target no primer on the reads decided follows the sheet's primers: the
 * amplicon they are registered for, or the one both they and the reads' primers name. Without either it
 * stays undetermined, and asked. Once the order exists, a contradiction is asked about, never followed.
 */
export const withResolvedTarget = (draft: DraftState): DraftState => {
  const settings = draft.settings
  const current = settings?.targetTaxonomy
  if (!settings || !current || draft.orderId || !(current.source === 'undetermined' || (current.source === 'detected' && current.via === 'sheet'))) {
    return draft
  }
  if (sheetPrimerPairs(draft.sheet) === undefined) {
    return draft
  }
  const sheet = sheetFinding(draft)
  const shared = readsFinding(draft).candidates.filter((candidate) => sheet.candidates.includes(candidate))
  const taxon = sheet.taxon ?? (shared.length === 1 ? shared[0] : undefined)
  const resolved: PlannedOrderSettings['targetTaxonomy'] = taxon
    ? { value: taxon, source: 'detected', via: 'sheet', reason: `the sheet's primers are registered for ${taxon}` }
    : {
        value: 'OTHER',
        source: 'undetermined',
        reason: current.source === 'undetermined' ? current.reason : 'no primers name a target'
      }
  if (current.value === resolved.value && current.source === resolved.source) {
    return draft
  }
  return { ...draft, settings: withTargetTaxonomy(settings, resolved, draft.discovery) }
}

/**
 * Before the order exists, a "trimmed" held back for its low confidence follows the sheet: primers it names
 * from the registry were looked for on the reads and are not there. Without such a sheet the format is
 * undetermined again, and asked; the lab's answer always stays.
 */
export const withResolvedFormat = (draft: DraftState): DraftState => {
  const { settings, discovery } = draft
  const current = settings?.sampleConfiguration
  if (!settings || !current || !discovery || draft.orderId || draft.trimmingAnswer || discovery.classifier.confidence !== 'low') {
    return draft
  }
  const pairs = sheetPrimerPairs(draft.sheet)
  if (!(current.source === 'undetermined' || current.via === 'sheet') || pairs === undefined) {
    return draft
  }
  const resolved = chooseSampleConfiguration(discovery, !!pairs)
  if (current.value === resolved.value && current.source === resolved.source) {
    return draft
  }
  return { ...draft, settings: { ...settings, sampleConfiguration: resolved } }
}

/** The amplicons to suggest when the lab picks the target: what the reads' and the sheet's primers are registered for. */
export const targetSuggestions = (draft: DraftState): TargetTaxonomyKey[] => [
  ...new Set([...readsFinding(draft).candidates, ...sheetFinding(draft).candidates])
]

/**
 * The sheet's primers are registered only for amplicons other than the order's target: submit would refuse
 * them, so the lab chooses. Not asked while the target is undetermined, which its own question covers.
 */
export const targetConflict = (draft: DraftState): Extract<WizardQuestion, { kind: 'target-conflict' }> | undefined => {
  const settings = draft.settings
  const pairs = sheetPrimerPairs(draft.sheet)
  if (!settings || !pairs || settings.targetTaxonomy.source === 'undetermined') {
    return undefined
  }
  const current = settings.targetTaxonomy.value
  if (pairs.amplicons.includes(current)) {
    return undefined
  }
  const from = settings.targetTaxonomy.source === 'answered' || settings.targetTaxonomy.source === 'entered' ? 'lab' : 'reads'
  return { id: 'target-conflict', kind: 'target-conflict', blocking: true, current, from, sheet: pairs }
}

export const readsTargetKept = (target: TargetTaxonomyKey): string => `reads-target-kept:${target}`

/**
 * R2 files settled a pair, where the R1 reads alone left it open, registered only for amplicons other than
 * the target: the target was chosen or detected before, so the lab settles it. Not where the sheet's primers
 * decide, which their own conflict covers.
 */
export const readsTargetConflict = (draft: DraftState): Extract<WizardQuestion, { kind: 'target-reads-conflict' }> | undefined => {
  const primers = draft.discovery?.primers
  const target = draft.settings?.targetTaxonomy
  if (primers?.status !== 'found' || !primers.fromMates || !target || target.source === 'undetermined' || sheetPrimerPairs(draft.sheet)) {
    return undefined
  }
  const amplicons = primers.pair.amplicons
  if (amplicons.includes(target.value) || (draft.acknowledged ?? []).includes(readsTargetKept(target.value))) {
    return undefined
  }
  return {
    id: 'target-reads-conflict',
    kind: 'target-reads-conflict',
    blocking: true,
    current: target.value,
    reads: { amplicons, names: primers.pair.names }
  }
}
